<template>
  <div style="height: 100%; display: flex; flex-direction: column;">
    <v-tabs-items ref="tabs-items" style="height: 100%; width: 100%;" v-model="currentTableTab">
      <v-tab-item
        transition="gb-table-transition"
        :value="tab.value"
        style="height: 100%; width: 100%;"
        v-for="tab in tableTabs"
        :key="tab.value"
      >
        <template>
          <component
            :is="getTabComponent(tab.value)"
            ref="table"
            :tabTitle="tab.name"
            :tab-info="tab"
            :class="{ 'no-print': isQuickPrint }"
          ></component>
        </template>
      </v-tab-item>
    </v-tabs-items>
    <div v-if="tableTabs.length > 1" class="table-tabs-wrapper">
      <v-tabs v-model="currentTableTab" class="table-tabs" height="43" hide-slider>
        <v-tab
          v-for="tab in tableTabs"
          :tab-value="tab.value"
          class="text-body-2 font--text font-weight-regular px-4"
          :key="tab.value"
        >
          <span style="position: absolute; top: 0; right: 0;">
            <v-btn
              class="x-button"
              :class="{ 'no-print': isQuickPrint }"
              v-if="!tab.preventClose"
              x-small
              icon
              color="rgb(196, 196, 196)"
              @click="closeTableTab(tab.value)"
            >
              <v-icon color="font" size="16">
                mdi-close
              </v-icon>
            </v-btn>
          </span>
          <div :id="`table-tab-${tab.value}`" class="d-flex" style="min-width: 0;">
            <span
              :ref="`table-tab-title-${tab.value}`"
              class="px-2 text-truncate text-caption"
              style="white-space: nowrap"
            >
              {{ tab.name }}
            </span>
            <span v-if="tab.appendName" class="text-caption">{{ tab.appendName }}</span>
            <v-tooltip :activator="`#table-tab-${tab.value}`" top color="font" v-if="tabsTooltips.includes(tab.value)">
              {{ tab.name }} {{ tab.appendName ?? '' }}
            </v-tooltip>
          </div>
        </v-tab>
      </v-tabs>
    </div>
  </div>
</template>

<script>
import { get, sync } from 'vuex-pathify';

import { capitalize } from '@/assets/js/utility';
import { camelCase } from 'lodash';

const components = {};
const requireComponent = require.context('@/components', false, /Table(?!Handler)(\w+|)\.(vue|js)$/);
const additionalTableTabsComponents = require.context('@/components', false, /TabTable(\w+|)\.(vue|js)$/);
[...requireComponent.keys(), ...additionalTableTabsComponents.keys()].forEach(fileName => {
  const componentConfig = requireComponent(fileName);

  const componentName = capitalize(
    camelCase(
      fileName
        .split('/')
        .pop()
        .replace(/\.\w+$/, '')
    )
  );
  components[componentName] = componentConfig.default;
});

export default {
  name: 'TableHandler',
  components,
  data: () => ({
    isTableComponentVisible: true,
    tabsTooltips: [],
  }),
  methods: {
    closeTableTab(value) {
      this.$store.set('table/CLOSE_TABLE_TAB!', value);
    },
    getTabComponent(value) {
      const dynamicNamesDict = {
        attributesTable: this.component,
        rziDocs: 'TabTableRziDocumentation',
        rziMonitoring: 'TabTableRziMonitoring',
      };
      const dynamicName = Object.entries(dynamicNamesDict).find(([key]) => value.includes(key))?.[1];
      if (!dynamicName) throw Error(`No matching component for '${value}' declared in TableHandler`);
      return dynamicName;
    },
    async redrawTableComponent() {
      this.isTableComponentVisible = false;
      await this.$nextTick();
      this.isTableComponentVisible = true;
    },
  },
  computed: {
    layers: get('layers/layers'),
    currentLayer: sync('layers/currentLayer'),
    tableTabs: get('table/tabs'),
    currentTableTab: sync('table/currentTableTab'),
    currentLayerDataSourceName: sync('layers/currentLayer@data_source_name'),
    watermetersDataSource: get('admin/modulesMapping@billing.watermeters_datasource_name'),
    failuresDataSource: get('admin/modulesMapping@failures.datasource_name'),
    zdmFailuresDataSource: get('admin/modulesMapping@zdm_failures.datasource_name'),
    tasksDataSource: get('admin/modulesMapping@tasks.datasource_name'),
    easementsDataSource: get('admin/modulesMapping@transmission_easement.datasource_name'),
    foldersDataSource: get('admin/modulesMapping@folders.datasource_name'),
    investmentsDataSource: get('admin/modulesMapping@investments.datasource_name'),
    currentLayerDataSource: get('layers/currentLayer@data_source_name'),
    dmaLayerId: get('admin/modulesMapping@billing.dma_layer_id'),
    easementLayerId: get('admin/modulesMapping@transmission_easement.layer_id'),
    investmentsLayerId: get('admin/modulesMapping@investments.layer_id'),
    tasksLayerId: get('admin/modulesMapping@tasks.layer_id'),
    failuresLayerId: get('admin/modulesMapping@failures.layer_id'),
    zdmFailuresLayerId: get('admin/modulesMapping@zdm_failures.layer_id'),
    foldersLayerId: get('admin/modulesMapping@folders.layer_id'),
    watermetersLayerId: get('admin/modulesMapping@billing.layer_id'),
    isQuickPrint: get('tools/toolStatus@isQuickPrintToolActive'),
    layerId() {
      if (this.$route.params.lid) {
        return this.$route.params.lid;
      }
      const dict = {
        cutoffLayerTable: 'TableCutoffs',
        lockAnalysis: this.failuresLayerId,
        failure: this.failuresLayerId,
        zdm_failure: this.zdmFailuresLayerId,
        zdm_failures: this.zdmFailuresLayerId,
        failures: this.failuresLayerId,
        newFailure: this.failuresLayerId,
        task: this.tasksLayerId,
        tasks: this.tasksLayerId,
        transmission_easements: this.easementLayerId,
        transmission_easement: this.easementLayerId,
        folders: this.foldersLayerId,
        folder: this.foldersLayerId,
        investments: this.investmentsLayerId,
        investment: this.investmentsLayerId,
        legalization: this.watermetersLayerId,
        legalizationInfo: this.watermetersLayerId,
        waterDissolutionWatermeters: this.watermetersLayerId,
        waterDissolutionWatermetersInfo: this.watermetersLayerId,
        waterDissolutionDma: this.dmaLayerId,
        waterDissolutionDmaInfo: this.dmaLayerId,
        billingAnomalyDetection: this.watermetersLayerId,
        billingAnomalyDetectionInfo: this.watermetersLayerId,
      };
      return dict[this.currentRouteName];
    },
    currentRouteName() {
      return this.$route.name;
    },
    currentLayerId() {
      return this.$route.params.lid;
    },
    component() {
      const dataSourceNameDict = {
        [this.failuresDataSource]: 'TableFailures',
        [this.zdmFailuresDataSource]: 'TableZdmFailures',
        [this.tasksDataSource]: 'TableTasks',
        [this.easementsDataSource]: 'TableEasements',
        [this.foldersDataSource]: 'TableFolders',
        [this.investmentsDataSource]: 'TableInvestments',
      };
      if (dataSourceNameDict[this.currentLayerDataSourceName]) {
        return dataSourceNameDict[this.currentLayerDataSourceName];
      }
      const dict = {
        cutoffLayerTable: 'TableCutoffs',
        lockAnalysis: 'TableFailures',
        failure: 'TableFailures',
        zdm_failure: 'TableZdmFailures',
        zdm_failures: 'TableZdmFailures',
        failures: 'TableFailures',
        newFailure: 'TableFailures',
        task: 'TableTasks',
        tasks: 'TableTasks',
        transmission_easements: 'TableEasements',
        transmission_easement: 'TableEasements',
        folders: 'TableFolders',
        folder: 'TableFolders',
        investments: 'TableInvestments',
        investment: 'TableInvestments',
        legalization: 'TableWatermeters',
        legalizationInfo: 'TableWatermeters',
        waterDissolutionDma: 'TableDma',
        waterDissolutionDmaInfo: 'TableDma',
        waterDissolutionWatermeters: 'TableWatermeters',
        waterDissolutionWatermetersInfo: 'TableWatermeters',
        billingAnomalyDetection: 'TableWatermeters',
        billingAnomalyDetectionInfo: 'TableWatermeters',
      };
      if (dict[this.currentRouteName]) {
        return dict[this.currentRouteName];
      }
      return 'TableBasic';
    },
  },
  watch: {
    tableTabs: {
      deep: true,
      async handler(nV) {
        if (nV.length <= 1) return (this.tabsTooltips = []);
        await this.$nextTick();
        this.tabsTooltips = nV.reduce((acc, tab) => {
          const tabTitleEl = this.$refs[`table-tab-title-${tab.value}`]?.[0];
          if (tabTitleEl?.scrollWidth > tabTitleEl?.clientWidth) acc.push(tab.value);
          return acc;
        }, []);
      },
    },
    layerId: {
      immediate: true,
      handler(nV, oV) {
        if (+nV !== +oV) this.currentTableTab = 'attributesTable';
        if (!nV) {
          this.currentLayer = undefined;
          return;
        }
        this.currentLayer = this.layers[nV];
      },
    },
  },
  beforeDestroy() {
    this.$store.set('table/RESET_TABLE_TABS!');
    this.currentLayer = undefined;
  },
  mounted() {
    this.$root.$on('redrawTableComponent', this.redrawTableComponent);
  },
};
</script>

<style scoped lang="scss">
.table-tabs-wrapper {
  height: 43px;
  width: 100%;
  border-top: 1px solid #e2e2e2;
  box-sizing: content-box;
  ::v-deep {
    .v-slide-group__next {
      right: 0;
    }
    .v-slide-group__prev {
      left: 0;
    }
    .v-slide-group__prev,
    .v-slide-group__next {
      position: absolute;
      top: 0;
      height: 100%;
      z-index: 1;
      i {
        background-color: #fff;
        border-radius: 100%;
      }
    }
  }
}

::v-deep .table-tabs {
  .v-tab--active::before {
    background-color: white;
    border: none;
  }
  .v-tab {
    border-right: 1px solid #e2e2e2;
    background-color: #f3f3f3;
    text-transform: none;
    font-weight: 500 !important;
    color: rgb(0, 0, 0, 0.3) !important;
    .x-button {
      opacity: 0.3;
    }
  }
  .v-tab--active {
    background-color: #fff !important;
    color: #000 !important;

    .x-button {
      opacity: 1;
    }
  }
}

/**
* Vue's transition workaround for two tabs overlapping for a split second
*/
.gb-table-transition-enter,
.gb-table-transition-leave-to {
  display: none;
}
</style>
